// Sequencing form `(do expr1 expr2 …)` — evaluates each form in turn
// and returns the last value. Useful for multi-step `if` / `case`
// branches.
//
// Threading macros `->` (thread-first) and `->>` (thread-last) are
// **pure syntactic sugar**: they are desugared at normalize time
// (`normalize.ts`'s `desugarThread`) into nested op-calls, never reach
// the engine as op-calls, and therefore have no runtime apply hook
// here. The single source of truth for threading semantics lives in
// `normalize.ts`; this file deliberately does not re-register `->` /
// `->>` against `opMeta`.

import { z } from 'zod';

import type { EvalResult } from '../env.ts';
import { opMeta, type SpecialCtx } from '../op-meta.ts';
import { paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import type { Value } from '../value.ts';

/**
 * Sequence forms. `(do expr1 expr2 …)` evaluates each form in turn in
 * the caller's env and returns the last value. The body must be
 * non-empty — an empty `(do)` has no defined return.
 */
export const doOp = z
  .object({
    name: z.literal('do'),
    params: z.array(astArg()).min(1),
  })
  .describe('Sequence one or more expressions and return the last value. `(do e1 e2 …)`.')
  .register(opMeta, {
    docHidden: true,
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(do (def x 1) (+ x 2))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const forms = data.params;
      // Sequential reduce; each form may def/let new names that subsequent
      // forms see, but the do form itself returns the caller's env so
      // those names don't leak past the body (consistent with `let`).
      const finalValue = await forms.reduce<Promise<Value>>(async (priorP, form, i) => {
        await priorP;
        const { value } = await ctx.evalNode(form, ctx.env, paramPath(ctx.path, i));
        return value as Value;
      }, Promise.resolve(null));
      return { value: finalValue, env: ctx.env };
    },
  });
